/* eslint-disable max-len */
import {ReloadOutlined} from '@ant-design/icons';
import {Col, Flex, Button, Form, InputNumber, Progress, Radio, Row, Select} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import Loader from '../../../components/Loader/Loader';
import PageContent from '../../../components/PageContent/PageContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import {IMPACT_FIELD_HINTS} from '../constants/impactFieldHints';
import {impactInitialValues} from '../constants/pcfDefaultValues';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  GWP_HORIZONS,
  GWP100_HORIZON_VALUE,
  BIOGENIC_OPTIONS,
  PCF_STANDARD_EN_15804_VALUE,
  PCF_STANDARD_PEF_VALUE
} from '../constants/pcfOptions';
import {useExpertMode} from '../hooks/useExpertMode';
import usePCFPersist from '../hooks/usePCFPersist';
import {usePCFStepHydration} from '../hooks/usePCFStepHydration';
import {
  ImpactConfigurationKeys,
  ImpactConfigurationLabels
} from '../PCFKeyLabels';

import {FormItemLabel} from './FormItemLabel';
import {
  biogenicHandlingRules,
  gwpHorizonRules,
  hasBioBasedMaterials,
  hasSequestrationRelevantStages,
  isImpactConfigurationReady,
  MOCK_CALCULATION_DELAY_MS,
  uncertaintyIterationsRules
} from './impactValidation';
import PCFFooterBackButton from './PCFFooterBackButton';
import PCFFooterPrimaryButton from './PCFFooterPrimaryButton';
import {runPCFDraftSave} from './pcfFormDraft';
import PCFFormHeader from './PCFFormHeader';
import {
  getImpactConfigurationPresets,
  getLciaMethodsForPcfStandard,
  getNonExpertImpactPresets,
  lciaMethodRules,
  shouldShowBiogenicField,
  shouldShowLciaMethodField
} from './pcfImpactConfiguration';
import TabStepper from './TabStepper';
import Toggle from './Toggle';

const impactValidateTrigger = ['onSubmit'];

const ImpactConfigurationPage: React.FC = () => {
  const [form] = Form.useForm();
  const [submitFailed, setSubmitFailed] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState(0);

  const {
    recordId,
    isStepReady,
    navigateToStep,
    reloadFromServer
  } = usePCFStepHydration(form, 'impactConfiguration');
  const {isSaving, saveDraftLabel, actionLabel, persist, runWhileSaving} =
    usePCFPersist();
  const expertMode = useExpertMode();
  const pcfStandard = usePCFStore(
    (state) => state.recordData.goalAndScope?.pcfStandard
  );
  const lifeCycleStages = usePCFStore(
    (state) => state.recordData.goalAndScope?.stages
  );
  const a1Materials = usePCFStore(
    (state) => state.recordData.lci?.a1?.materials
  );

  const lciaMethodOptions = useMemo(
    () => getLciaMethodsForPcfStandard(pcfStandard),
    [pcfStandard]
  );

  const gwpHorizonOptions = useMemo(
    () =>
      expertMode
        ? GWP_HORIZONS
        : GWP_HORIZONS.filter((option) => option.value === GWP100_HORIZON_VALUE),
    [expertMode]
  );

  const showLciaMethodField = shouldShowLciaMethodField(expertMode, pcfStandard);
  const showBiogenicField = shouldShowBiogenicField(expertMode, pcfStandard);
  const showSequestrationField =
    expertMode && hasSequestrationRelevantStages(lifeCycleStages);

  useEffect(() => {
    const presets = expertMode
      ? getImpactConfigurationPresets(pcfStandard)
      : getNonExpertImpactPresets(pcfStandard);

    if (Object.keys(presets).length > 0) {
      form.setFieldsValue(presets);
    }
  }, [form, pcfStandard, expertMode]);

  useEffect(() => {
    if (!showSequestrationField) {
      form.setFieldValue(ImpactConfigurationKeys.CARBON_SEQUESTRATION, false);
    }
  }, [form, showSequestrationField]);

  const lciaMethod = Form.useWatch(ImpactConfigurationKeys.LCIA_METHOD, form);
  const gwpHorizon = Form.useWatch(ImpactConfigurationKeys.GWP_TIME_HORIZON, form);
  const biogenicHandling = Form.useWatch(
    ImpactConfigurationKeys.BIOGENIC_CARBON_HANDLING,
    form
  );
  const lucEnabled = Form.useWatch(ImpactConfigurationKeys.LUC_EMISSIONS, form);
  const sequestrationEnabled = Form.useWatch(
    ImpactConfigurationKeys.CARBON_SEQUESTRATION,
    form
  );
  const uncertaintyEnabled = Form.useWatch(
    ImpactConfigurationKeys.UNCERTAINTY_ANALYSIS,
    form
  );

  const showGwpDisclosure =
    gwpHorizon && gwpHorizon !== GWP100_HORIZON_VALUE;
  const showLucPrompt =
    expertMode && hasBioBasedMaterials(a1Materials) && !lucEnabled;
  const isEn15804Standard = pcfStandard === PCF_STANDARD_EN_15804_VALUE;
  const isPefStandard = pcfStandard === PCF_STANDARD_PEF_VALUE;
  const isFormReady = isImpactConfigurationReady({
    lciaMethod,
    gwpHorizon,
    biogenicHandling
  });

  const persistImpact = async (status: 'draft' | 'approved'): Promise<void> => {
    await persist({
      section: 'impactConfiguration',
      sectionValues: form.getFieldsValue(true),
      status
    });
  };

  const runMockCalculation = async (): Promise<void> => {
    setIsCalculating(true);
    setCalcProgress(0);

    const steps = 10;
    const stepDelay = MOCK_CALCULATION_DELAY_MS / steps;

    for (let step = 1; step <= steps; step += 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, stepDelay);
      });
      setCalcProgress(Math.round((step / steps) * 100));
    }
  };

  const handleSaveDraft = (): void => {
    runWhileSaving(async () => {
      setSubmitFailed(false);
      await runPCFDraftSave(form, async () => {
        await persistImpact('draft');
      });
    }).catch(() => {});
  };

  const handleCalculate = (): void => {
    runWhileSaving(async () => {
      await form.validateFields();
      setSubmitFailed(false);
      await runMockCalculation();
      await persistImpact('approved');
      navigateToStep('resultsInterpretation');
    }).catch(() => {
      setSubmitFailed(true);
    }).finally(() => {
      setIsCalculating(false);
      setCalcProgress(0);
    });
  };

  const showFormErrorSummary =
    submitFailed && form.getFieldsError().some(({errors}) => errors.length > 0);

  return (
    <Flex
      vertical
      justify="space-between"
      align="center"
      gap={24}
      style={{height: '100%'}}
    >
      <Flex
        vertical={false}
        justify="space-between"
        align="center"
        style={{width: '100%'}}
      >
        <MainTableTitle
          title="Product Carbon Footprint (PCF)"
          showDivider={false}
        />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => {
              reloadFromServer().catch(() => {});
            }}
            disabled={isSaving || isCalculating}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSaveDraft}
            loading={isSaving}
            disabled={isSaving || isCalculating}
          >
            {saveDraftLabel}
          </Button>
        </Flex>
      </Flex>
      <TabStepper activeStep={3} completedSteps={[1, 2]} recordId={recordId} />
      <PageContent>
        <div className="impact-configuration-page">
          {!isStepReady ? (
            <Loader topMargin />
          ) : (
            <>
              <PCFFormHeader
                title="3 . Impact Assessment Configuration"
                description="Confirm methodology and run the calculation engine."
              />

              <section className="card">
                <div className="card-body">
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={impactInitialValues}
                    validateTrigger={impactValidateTrigger}
                    onValuesChange={() => {
                      if (submitFailed) setSubmitFailed(false);
                    }}
                  >
                    {!showLciaMethodField && (
                      <Form.Item
                        name={ImpactConfigurationKeys.LCIA_METHOD}
                        hidden
                        rules={lciaMethodRules(pcfStandard)}
                      >
                        <input type="hidden" />
                      </Form.Item>
                    )}
                    {!showBiogenicField && (
                      <Form.Item
                        name={ImpactConfigurationKeys.BIOGENIC_CARBON_HANDLING}
                        hidden
                        rules={biogenicHandlingRules()}
                      >
                        <input type="hidden" />
                      </Form.Item>
                    )}

                    <Row gutter={rowGutter}>
                      {showLciaMethodField && (
                        <Col
                          className={PCF_COL_GUTTER_CLASS}
                          span={PCF_COL_SPAN_HALF}
                        >
                          <Form.Item
                            name={ImpactConfigurationKeys.LCIA_METHOD}
                            required
                            label={
                              <FormItemLabel
                                hint={IMPACT_FIELD_HINTS.lciaMethod}
                              >
                                {ImpactConfigurationLabels.LCIA_METHOD}
                              </FormItemLabel>
                            }
                            rules={lciaMethodRules(pcfStandard)}
                            validateFirst
                            hasFeedback
                          >
                            <Select
                              className="select"
                              placeholder="Select"
                              style={{width: '100%'}}
                              options={lciaMethodOptions}
                              disabled={isEn15804Standard || isPefStandard}
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={showLciaMethodField ? PCF_COL_SPAN_HALF : 24}
                      >
                        <Form.Item
                          name={ImpactConfigurationKeys.GWP_TIME_HORIZON}
                          required
                          label={
                            <FormItemLabel hint={IMPACT_FIELD_HINTS.gwpHorizon}>
                              {ImpactConfigurationLabels.GWP_TIME_HORIZON}
                            </FormItemLabel>
                          }
                          rules={gwpHorizonRules(expertMode)}
                          validateFirst
                          hasFeedback
                        >
                          <Radio.Group
                            className="gwp-horizon-radios"
                            options={gwpHorizonOptions}
                          />
                        </Form.Item>
                      </Col>
                      {showBiogenicField && (
                        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
                          <Form.Item
                            name={
                              ImpactConfigurationKeys.BIOGENIC_CARBON_HANDLING
                            }
                            required
                            label={
                              <FormItemLabel
                                hint={IMPACT_FIELD_HINTS.biogenicHandling}
                              >
                                {
                                  ImpactConfigurationLabels.BIOGENIC_CARBON_HANDLING
                                }
                              </FormItemLabel>
                            }
                            rules={biogenicHandlingRules()}
                            validateFirst
                            hasFeedback
                          >
                            <Select
                              className="select"
                              placeholder="Select"
                              style={{width: '100%'}}
                              options={BIOGENIC_OPTIONS}
                              disabled={isEn15804Standard}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>

                    {expertMode ? (
                      <div className="toggle-row">
                        <div className="toggle-label">
                          <FormItemLabel hint={IMPACT_FIELD_HINTS.luc}>
                            {ImpactConfigurationLabels.LUC_EMISSIONS}
                          </FormItemLabel>
                        </div>
                        <Form.Item
                          name={ImpactConfigurationKeys.LUC_EMISSIONS}
                          valuePropName="checked"
                          noStyle
                        >
                          <Toggle />
                        </Form.Item>
                      </div>
                    ) : (
                      <Form.Item
                        name={ImpactConfigurationKeys.LUC_EMISSIONS}
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>
                    )}

                    {showSequestrationField ? (
                      <div className="toggle-row">
                        <div className="toggle-label">
                          <FormItemLabel hint={IMPACT_FIELD_HINTS.sequestration}>
                            {ImpactConfigurationLabels.CARBON_SEQUESTRATION}
                          </FormItemLabel>
                        </div>
                        <Form.Item
                          name={ImpactConfigurationKeys.CARBON_SEQUESTRATION}
                          valuePropName="checked"
                          noStyle
                        >
                          <Toggle />
                        </Form.Item>
                      </div>
                    ) : (
                      <Form.Item
                        name={ImpactConfigurationKeys.CARBON_SEQUESTRATION}
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>
                    )}

                    <div className="ef-database-block">
                      <Form.Item
                        label={
                          <FormItemLabel
                            hint={IMPACT_FIELD_HINTS.efDatabaseVersion}
                          >
                            {ImpactConfigurationLabels.EF_DATABASE_VERSION}
                          </FormItemLabel>
                        }
                      >
                        <div className="ef-panel">
                          <div className="ef-row">
                            <strong>ecoinvent version used:</strong> 3.10
                          </div>
                          <div className="ef-row">
                            <strong>Bharat Carbon India DB version:</strong>{' '}
                            BC-IND-2026.03
                          </div>
                          <div className="ef-row">
                            <strong>CEA grid EF year:</strong> 2023
                          </div>
                          <div className="ef-row">
                            <strong>IPCC AR version:</strong> AR6
                          </div>
                        </div>
                      </Form.Item>
                    </div>

                    {expertMode ? (
                      <>
                        <div className="toggle-row">
                          <div className="toggle-label">
                            <FormItemLabel hint={IMPACT_FIELD_HINTS.uncertainty}>
                              {ImpactConfigurationLabels.UNCERTAINTY_ANALYSIS}
                            </FormItemLabel>
                          </div>
                          <Form.Item
                            name={ImpactConfigurationKeys.UNCERTAINTY_ANALYSIS}
                            valuePropName="checked"
                            noStyle
                          >
                            <Toggle />
                          </Form.Item>
                        </div>

                        {uncertaintyEnabled && (
                          <Row gutter={rowGutter}>
                            <Col
                              className={PCF_COL_GUTTER_CLASS}
                              span={PCF_COL_SPAN_HALF}
                            >
                              <Form.Item
                                name={
                                  ImpactConfigurationKeys.UNCERTAINTY_ITERATIONS
                                }
                                required
                                label={
                                  <FormItemLabel
                                    hint={
                                      IMPACT_FIELD_HINTS.uncertaintyIterations
                                    }
                                  >
                                    {
                                      ImpactConfigurationLabels.UNCERTAINTY_ITERATIONS
                                    }
                                  </FormItemLabel>
                                }
                                rules={uncertaintyIterationsRules()}
                                validateFirst
                                hasFeedback
                              >
                                <InputNumber
                                  controls={false}
                                  className="input"
                                  style={{width: '100%'}}
                                  min={100}
                                  max={10000}
                                  precision={0}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        )}
                      </>
                    ) : (
                      <>
                        <Form.Item
                          name={ImpactConfigurationKeys.UNCERTAINTY_ANALYSIS}
                          hidden
                        >
                          <input type="hidden" />
                        </Form.Item>
                        <Form.Item
                          name={ImpactConfigurationKeys.UNCERTAINTY_ITERATIONS}
                          hidden
                        >
                          <input type="hidden" />
                        </Form.Item>
                      </>
                    )}

                    {showLucPrompt && (
                      <div className="alert-amber">
                        <div className="alert-title">Smart prompt</div>
                        <div className="alert-body">
                          Your study includes bio-based materials. ISO 14067
                          requires LUC emissions to be considered and reported.
                        </div>
                      </div>
                    )}

                    {showGwpDisclosure && (
                      <div className="alert-amber">
                        <div className="alert-title">
                          Mandatory disclosure note
                        </div>
                        <div className="alert-body">
                          ISO 14067 requires GWP100 as the primary reported
                          metric. Non-100-year results must be reported as
                          supplementary information only.
                        </div>
                      </div>
                    )}

                    {sequestrationEnabled && (
                      <div className="alert-amber">
                        <div className="alert-title">
                          Mandatory disclosure note
                        </div>
                        <div className="alert-body">
                          ISO 14067 §7.3.5 governs temporary carbon storage
                          accounting. A mandatory disclosure note will be added to
                          the report.
                        </div>
                      </div>
                    )}

                    {uncertaintyEnabled && expertMode && (
                      <div className="alert-amber alert-info">
                        <div className="alert-title">Calculation time warning</div>
                        <div className="alert-body">
                          Uncertainty analysis is enabled. The calculation will
                          take longer than a standard point-estimate run.
                        </div>
                      </div>
                    )}

                    {isCalculating && (
                      <div className="calc-progress-block">
                        <div className="calc-progress-label">
                          Running PCF calculation engine…
                        </div>
                        <Progress
                          percent={calcProgress}
                          status="active"
                          showInfo
                          strokeColor={{from: '#719a5d', to: '#435b37'}}
                        />
                      </div>
                    )}
                  </Form>
                </div>
              </section>

              {showFormErrorSummary && (
                <div className="form-error-summary">
                  Please complete all mandatory fields marked with * before
                  calculating.
                </div>
              )}

              <div className="footer-actions">
                <PCFFooterBackButton
                  disabled={isSaving || isCalculating}
                  onClick={() => {
                    navigateToStep('lciDataCollection');
                  }}
                />
                <PCFFooterPrimaryButton
                  onClick={handleCalculate}
                  label={
                    isCalculating
                      ? 'Calculating PCF…'
                      : actionLabel('Calculate PCF')
                  }
                  isSaving={isSaving || isCalculating}
                  disabled={!isFormReady || isCalculating}
                />
              </div>
            </>
          )}
        </div>
      </PageContent>
    </Flex>
  );
};

export default ImpactConfigurationPage;
